import api from '@/services/api';
import type { Certificate, CertificateInput, CertificateTemplate, CertificateTemplateInput } from '@/types/certificate_schema';

const BASE_URL = '/certificates';
const TEMPLATE_URL = '/certificates/templates';

export const certificateApi = {
  // ========= CERTIFICATES =========

  /**
   * Get all certificates
   */
  getAllCertificates: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    student_id?: string;
    course_id?: string;
    batch_id?: string;
    status?: string;
  }) => {
    return api.get<{ data: Certificate[]; meta?: any }>(BASE_URL, { params });
  },

  /**
   * Get a single certificate by ID
   */
  getCertificateById: async (id: string) => {
    return api.get<{ data: Certificate }>(`${BASE_URL}/${id}`);
  },

  /**
   * Verify a certificate by verification code
   */
  verifyCertificate: async (verificationCode: string) => {
    return api.get<{ data: Certificate; valid: boolean }>(`${BASE_URL}/verify/${verificationCode}`);
  },

  /**
   * Issue a new certificate
   */
  issueCertificate: async (input: CertificateInput) => {
    return api.post<{ data: Certificate }>(BASE_URL, input);
  },

  /**
   * Bulk issue certificates
   */
  bulkIssueCertificates: async (inputs: CertificateInput[]) => {
    return api.post<{ data: Certificate[] }>(`${BASE_URL}/bulk`, { certificates: inputs });
  },

  /**
   * Revoke a certificate
   */
  revokeCertificate: async (id: string, reason?: string) => {
    return api.post<{ data: Certificate }>(`${BASE_URL}/${id}/revoke`, { reason });
  },

  /**
   * Regenerate certificate PDF
   */
  regeneratePdf: async (id: string) => {
    return api.post<{ data: Certificate }>(`${BASE_URL}/${id}/regenerate-pdf`);
  },

  /**
   * Download certificate PDF
   */
  downloadPdf: async (id: string) => {
    return api.get(`${BASE_URL}/${id}/download`, { responseType: 'blob' });
  },

  // ========= TEMPLATES =========

  /**
   * Get all certificate templates
   */
  getAllTemplates: async (params?: {
    page?: number;
    per_page?: number;
    is_active?: boolean;
  }) => {
    return api.get<{ data: CertificateTemplate[]; meta?: any }>(TEMPLATE_URL, { params });
  },

  /**
   * Get a single template by ID
   */
  getTemplateById: async (id: string) => {
    return api.get<{ data: CertificateTemplate }>(`${TEMPLATE_URL}/${id}`);
  },

  /**
   * Create a new template
   */
  createTemplate: async (input: CertificateTemplateInput) => {
    return api.post<{ data: CertificateTemplate }>(TEMPLATE_URL, input);
  },

  /**
   * Update a template
   */
  updateTemplate: async (id: string, input: Partial<CertificateTemplateInput>) => {
    return api.put<{ data: CertificateTemplate }>(`${TEMPLATE_URL}/${id}`, input);
  },

  /**
   * Delete a template
   */
  deleteTemplate: async (id: string) => {
    return api.delete(`${TEMPLATE_URL}/${id}`);
  },

  /**
   * Set template as default
   */
  setDefaultTemplate: async (id: string) => {
    return api.post<{ data: CertificateTemplate }>(`${TEMPLATE_URL}/${id}/set-default`);
  },
};

export default certificateApi;
